import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { createRecognizer, liveTextSupported, loadModel } from '../lib/liveRecognizer'
import { grammarWords } from '../lib/repeatMatch'

/**
 * The live text of one repetition (R13): the words the on-device recognizer
 * hears while the learner speaks, typed as they come.
 *
 * `status` is `off` (the practice does not repeat), `loading` (the model is
 * being fetched — once per browser, ~40 MB), `ready`, or `unavailable` (no
 * WebAssembly or workers, or the model could not be had). `unavailable` is
 * not an error the learner can fix: the practice then counts a repetition by
 * voice alone (`speechHeard`) and says that the live text is off.
 *
 * The microphone hands every quantum to `feed`; the first one builds the
 * repetition's recognizer at the microphone's own rate. `begin()` before a
 * repetition clears the last one's words; `end()` after it resolves to what
 * was heard — or null when there was no recognizer, the signal to fall back.
 */
export function useLiveText(textEn, enabled) {
  const [status, setStatus] = useState(enabled ? 'loading' : 'off')
  const [words, setWords] = useState([])
  const modelRef = useRef(null)
  const recognizerRef = useRef(null)
  // Every way the sentence's words may be said — as written and in the one
  // spelling of the «all words» rule («I'm» and «i am», «2» and «two»).
  const target = useMemo(() => grammarWords(textEn), [textEn])

  useEffect(() => {
    if (!enabled) {
      setStatus('off')
      return undefined
    }
    if (!liveTextSupported()) {
      setStatus('unavailable')
      return undefined
    }
    let cancelled = false
    setStatus('loading')
    void loadModel().then((model) => {
      if (cancelled) return
      modelRef.current = model
      setStatus(model ? 'ready' : 'unavailable')
    })
    return () => {
      cancelled = true
    }
  }, [enabled])

  const begin = useCallback(() => {
    recognizerRef.current?.close()
    recognizerRef.current = null
    setWords([])
  }, [])

  const feed = useCallback(
    (chunk, sampleRate) => {
      const model = modelRef.current
      if (!model) return
      if (!recognizerRef.current) {
        try {
          recognizerRef.current = createRecognizer(model, { words: target, sampleRate, onWords: setWords })
        } catch (error) {
          console.warn('[live text] recognizer could not start:', error)
          modelRef.current = null
          setStatus('unavailable')
          return
        }
      }
      recognizerRef.current.feed(chunk)
    },
    [target],
  )

  const end = useCallback(async () => {
    const recognizer = recognizerRef.current
    recognizerRef.current = null
    if (!recognizer) return null
    try {
      const heard = await recognizer.finish()
      setWords(heard)
      return heard
    } finally {
      recognizer.close()
    }
  }, [])

  useEffect(() => () => recognizerRef.current?.close(), [])

  return { status, words, target, begin, feed, end }
}
